/**
 * Filter store for transport mode toggles and zoom-based filtering.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';

import type { TransportMode } from '../types/api';
import { LOD_CONFIG } from '../config';

// =============================================================================
// TYPES
// =============================================================================

interface FilterStoreState {
  // Transport mode toggles
  enabledModes: Set<TransportMode>;

  // Current zoom level (for LOD filtering)
  zoomLevel: number;

  // Line filters (multiple lines can be shown simultaneously)
  lineFilters: string[];

  // Actions
  toggleMode: (mode: TransportMode) => void;
  setModeEnabled: (mode: TransportMode, enabled: boolean) => void;
  setZoomLevel: (zoom: number) => void;
  enableAllModes: () => void;
  disableAllModes: () => void;
  addLineFilter: (line: string) => void;
  removeLineFilter: (line: string) => void;
  clearLineFilters: () => void;

  // Computed
  isModeVisible: (mode: TransportMode) => boolean;
  getVisibleModes: () => TransportMode[];
  hasLineFilter: (lineName: string) => boolean;
}

// =============================================================================
// ALL TRANSPORT MODES
// =============================================================================

export const ALL_TRANSPORT_MODES: TransportMode[] = [
  'tram',
  'bus',
  'ferry',
  'train',
  'taxi',
  'teletaxi',
];

// =============================================================================
// STORE
// =============================================================================

export const useFilterStore = create<FilterStoreState>()(
  persist(
    immer((set, get) => ({
      // Default: all modes enabled
      enabledModes: new Set(ALL_TRANSPORT_MODES),
      zoomLevel: 12,
      lineFilters: [],

      toggleMode: (mode) => {
        set((state) => {
          if (state.enabledModes.has(mode)) {
            state.enabledModes.delete(mode);
          } else {
            state.enabledModes.add(mode);
          }
        });
      },

      setModeEnabled: (mode, enabled) => {
        set((state) => {
          if (enabled) {
            state.enabledModes.add(mode);
          } else {
            state.enabledModes.delete(mode);
          }
        });
      },

      setZoomLevel: (zoom) => {
        set((state) => {
          state.zoomLevel = zoom;
        });
      },

      enableAllModes: () => {
        set((state) => {
          state.enabledModes = new Set(ALL_TRANSPORT_MODES);
        });
      },

      disableAllModes: () => {
        set((state) => {
          state.enabledModes = new Set();
        });
      },

      addLineFilter: (line) => {
        set((state) => {
          const normalizedLine = line.trim().toUpperCase();
          if (normalizedLine && !state.lineFilters.includes(normalizedLine)) {
            state.lineFilters.push(normalizedLine);
          }
        });
      },

      removeLineFilter: (line) => {
        set((state) => {
          const normalizedLine = line.trim().toUpperCase();
          state.lineFilters = state.lineFilters.filter((f) => f !== normalizedLine);
        });
      },

      clearLineFilters: () => {
        set((state) => {
          state.lineFilters = [];
        });
      },

      isModeVisible: (mode) => {
        const state = get();

        // Check if mode is enabled by user
        if (!state.enabledModes.has(mode)) {
          return false;
        }

        // Check LOD threshold
        const threshold = LOD_CONFIG.ZOOM_THRESHOLDS[mode];
        return state.zoomLevel >= threshold;
      },

      getVisibleModes: () => {
        const state = get();
        return ALL_TRANSPORT_MODES.filter((mode) => state.isModeVisible(mode));
      },

      hasLineFilter: (lineName) => {
        const state = get();
        // If no filters, show all lines
        if (state.lineFilters.length === 0) {
          return true;
        }
        // Check if line matches any filter (case-insensitive)
        const normalizedName = lineName.toUpperCase();
        return state.lineFilters.some((filter) => normalizedName === filter);
      },
    })),
    {
      name: 'vasttrafik-filter-store',
      partialize: (state) => ({
        enabledModes: Array.from(state.enabledModes),
      }),
      merge: (persisted, current) => {
        const persistedState = persisted as { enabledModes?: TransportMode[] };
        return {
          ...current,
          enabledModes: persistedState?.enabledModes
            ? new Set(persistedState.enabledModes)
            : current.enabledModes,
        };
      },
    }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

export const selectEnabledModes = (state: FilterStoreState) => state.enabledModes;
export const selectZoomLevel = (state: FilterStoreState) => state.zoomLevel;
export const selectLineFilters = (state: FilterStoreState) => state.lineFilters;
